import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { MessageService } from '../../../../../global/services/message/message.abstract.service';

export interface Department {
    id: string;
    label: string;
    name?: { en: string; fr: string } | string;
}

export interface CreateSubjectDialogData {
    searchTerm?: string;
    categoryId?: string;
    currentLang?: string;
    departments?: Department[]; // Use the locally defined Department interface
    departmentId?: string;
}

@Component({
    selector: 'app-create-subject-dialog',
    templateUrl: './create-subject-dialog.component.html',
    styleUrls: ['./create-subject-dialog.component.scss'],
})
export class CreateSubjectDialogComponent implements OnInit, OnDestroy {
    subjectForm: FormGroup;

    isSubmitting = false;

    departments: Department[] = []; // Use the locally defined Department interface

    private destroy$ = new Subject<void>();

    currentLang: string;

    constructor(
        private fb: FormBuilder,
        private translate: TranslateService,
        private messageService: MessageService,
        public dialogRef: MatDialogRef<CreateSubjectDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: CreateSubjectDialogData,
    ) {
        this.currentLang = data.currentLang || this.translate.currentLang || 'en';
        this.departments = data.departments || [];

        // Initialize form
        this.initForm();

        // Make dialog close on backdrop click
        this.dialogRef
            .backdropClick()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.close();
            });
    }

    ngOnInit(): void {
        if (this.data.searchTerm) {
            const formData: any = {
                name: {
                    en: '',
                    fr: '',
                },
            };

            if (this.currentLang === 'en') {
                formData.name.en = this.data.searchTerm;
            } else if (this.currentLang === 'fr') {
                formData.name.fr = this.data.searchTerm;
            }

            this.subjectForm.patchValue(formData);
        }
        // Pre-fill departmentId if available in data and form exists
        if (this.data.departmentId && this.subjectForm) {
            this.subjectForm.patchValue({ departmentId: this.data.departmentId });
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private initForm(): void {
        this.subjectForm = this.fb.group({
            name: this.fb.group({
                en: ['', Validators.required],
                fr: ['', Validators.required],
            }),
            departmentId: [this.data.departmentId || null],
        });
    }

    createSubject(): void {
        if (!this.subjectForm.valid) {
            this.markFormGroupTouched(this.subjectForm);
            return;
        }

        this.isSubmitting = true;
        const formData = this.subjectForm.value;

        // Generate a temporary ID for the new subject
        const tempId = `temp_${new Date().getTime()}`;

        const newSubject = {
            id: tempId,
            value: tempId,
            name: {
                en: formData.name.en,
                fr: formData.name.fr,
            },
            label: formData.name[this.currentLang] || formData.name.en,
            departmentId: formData.departmentId,
        };

        setTimeout(() => {
            this.dialogRef.close(newSubject);
        }, 500); // Small delay to show loading state
    }

    close(): void {
        this.dialogRef.close();
    }

    // Helper method to mark all form controls as touched
    private markFormGroupTouched(formGroup: FormGroup): void {
        Object.values(formGroup.controls).forEach((control) => {
            control.markAsTouched();

            if (control instanceof FormGroup) {
                this.markFormGroupTouched(control);
            }
        });
    }
}
